<template>
    <Dropdown
        placement="bottom-start"
        transfer
        trigger="custom"
        :visible="dropdownIsVisible"
        @onClickoutside="toggleDropdown"
    >
        <hox-dropdown-button
            :is-active="dropdownIsVisible"
            :is-disabled="isDisabled || isInProgress"
            :is-loading="isSettingPublishedStatus"
            data-testid="library__publish-status-select"
            @click="toggleDropdown"
        >
            Publish
        </hox-dropdown-button>

        <template #list>
            <DropdownItem
                data-testid="library-doubleclick--publish"
                @click="
                    publishHandler({
                        publishProvider: PUBLISH_PROVIDERS.DOUBLECLICK,
                        isRendering: false,
                        rebuild: false
                    })
                "
            >
                Publish to {{ PlatformLabel[PLATFORM.DOUBLECLICK] }} feed
            </DropdownItem>
            <DropdownItem data-testid="library-doubleclick--unpublish" @click="unpublishHandler(PLATFORM.DOUBLECLICK)">
                Unpublish from {{ PlatformLabel[PLATFORM.DOUBLECLICK] }} feed
            </DropdownItem>
            <DropdownItem
                data-testid="library-doubleclick--rebuild"
                @click="rebuildHandler({ publishProvider: PUBLISH_PROVIDERS.DOUBLECLICK, isRendering: false })"
            >
                Rebuild {{ PlatformLabel[PLATFORM.DOUBLECLICK] }} feed
            </DropdownItem>
            <DropdownItem
                divided
                data-testid="library-open-media-studio--render"
                @click="rebuildHandler({ publishProvider: PUBLISH_PROVIDERS.OPENDC, isRendering: true })"
            >
                Render ads for {{ PlatformLabel[PLATFORM.OPENDC] }}
            </DropdownItem>
            <DropdownItem
                data-testid="library-open-media-studio--publish"
                :disabled="!showPublishToOpendc"
                @click="showPublishToOpendc && publishCOAdset()"
            >
                Send ads to {{ PlatformLabel[PLATFORM.OPENDC] }} adset
            </DropdownItem>
            <DropdownItem
                v-if="userCanPublishToFacebook"
                data-testid="library-facebook--publish"
                @click="showPublishModal(PLATFORM.FACEBOOK, 'publish')"
            >
                Publish to {{ PlatformLabel[PLATFORM.FACEBOOK] }}
            </DropdownItem>
        </template>
    </Dropdown>

    <hox-modal v-if="rebuildFeedModal" @close="clearRebuildFeedCallback">
        <template #header>Rebuild feed</template>
        <div class="rebuild-modal__info">Are you sure?</div>
        <div>
            Rebuilding the feed will unpublish existing deliverables matching the sizes of the current selection. The
            current selection will then be published to build a new feed for each size selected.
        </div>

        <template #footer>
            <Button @click="clearRebuildFeedCallback">Close</Button>
            <Button type="error" @click="rebuildFeedCallback">Rebuild</Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { PLATFORM, PUBLISH_PROVIDERS, type PublishProvidersType } from "hoxton-shared/enums/exports";
import { mapStores } from "pinia";

import type { ConstructedDeliverable, Err } from "@/types/types";

import HoxModal from "@/components/Modal/Modal/Modal.vue";
import HoxDropdownButton from "@/components/common/DropdownButton.vue";
import { NotificationStatus, NotificationTypes } from "@/enums/notifications";
import { PlatformLabel } from "@/enums/platforms";
import { publishChoreographFeed, publishDeliverables, unpublishDeliverables } from "@/services/deliverables/publish";
import { AuthGetters } from "@/store/modules/auth";
import { JobsAction, type JobNotification } from "@/store/modules/jobs";
import { NotificationsAction } from "@/store/modules/notifications";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";

export default defineComponent({
    components: {
        HoxModal,
        HoxDropdownButton
    },

    props: {
        deliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },

        isDisabled: {
            required: true,
            type: Boolean
        },

        itemToChangeNumber: {
            required: false,
            type: Number
        },

        showPublishToOpendc: {
            required: false,
            type: Boolean
        },

        lastJob: {
            required: false,
            type: Object as PropType<JobNotification>,
            default: null
        }
    },

    emits: ["showPublishModal", "publishedStatusHasBeenSet", "refetchDeliverables"],

    data() {
        return {
            dropdownIsVisible: false,
            isSettingPublishedStatus: false,
            rebuildFeedModal: false,
            rebuildFeedCallback: null as unknown as null | (() => void),
            PLATFORM,
            PUBLISH_PROVIDERS,
            PlatformLabel
        };
    },

    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),
        campaignId() {
            return this.$route.params.campaignId as string;
        },
        deliverablesCount() {
            return this.itemToChangeNumber || this.deliverables.length;
        },

        isCampaignLocked() {
            return this.$store.state.campaign.isLocked;
        },

        isInProgress() {
            return this.isSettingPublishedStatus || this.isCampaignLocked;
        },

        isSuperAdmin() {
            return this.$store.getters[AuthGetters.isSuperAdmin];
        },

        userCanPublishToFacebook() {
            const { fbAdsManagerConfig } = this.$store.state.campaign.client;
            return fbAdsManagerConfig && this.isSuperAdmin;
        }
    },

    methods: {
        clearRebuildFeedCallback() {
            this.rebuildFeedModal = false;
            this.rebuildFeedCallback = null;
        },

        showPublishModal(platform: PublishProvidersType, action: "publish" | "unpublish" | "rebuild") {
            this.$emit("showPublishModal", {
                platform,
                action
            });
            this.toggleDropdown();
        },

        rebuildHandler({
            publishProvider,
            isRendering
        }: {
            publishProvider: PublishProvidersType;
            isRendering: boolean;
        }) {
            this.toggleDropdown();
            this.rebuildFeedModal = true;
            this.rebuildFeedCallback = () => {
                this.publishHandler({
                    publishProvider,
                    isRendering,
                    rebuild: true
                });
                this.clearRebuildFeedCallback();
            };
        },

        async publishHandler({
            publishProvider,
            isRendering,
            rebuild
        }: {
            publishProvider: PublishProvidersType;
            isRendering: boolean;
            rebuild: boolean;
        }) {
            this.isSettingPublishedStatus = true;

            const adsText = `${this.deliverablesCount} ${this.deliverablesCount === 1 ? "ad" : "ads"}`;

            if (isRendering) {
                this.$snackbar.info(
                    `Rendering ${adsText} for ${this.PlatformLabel[publishProvider as keyof typeof this.PlatformLabel]}.<br/>Please check the notifications for updates.`
                );
            } else {
                this.$snackbar.info(
                    `Adding ${adsText} to ${this.PlatformLabel[publishProvider as keyof typeof this.PlatformLabel]} feeds`
                );
            }

            this.toggleDropdown();
            let job: JobNotification;

            try {
                job = await publishDeliverables({
                    campaignId: this.campaignId,
                    publishProvider,
                    deliverables: this.deliverables,
                    libraryFilters: this.creativeLibraryFiltersStore.getComputedLibraryFilters,
                    isRendering,
                    rebuild
                });
            } catch (err) {
                this.$snackbar.error((err as Err).message);
                return;
            } finally {
                this.isSettingPublishedStatus = false;
            }

            const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                deliverablesCount: this.deliverablesCount,
                platform: publishProvider,
                status: NotificationStatus.InProgress,
                type: NotificationTypes.Publish,
                job
            });

            this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                jobId: job?._id,
                notificationId
            });

            this.$emit("publishedStatusHasBeenSet");
        },

        async unpublishHandler(publishProvider: PublishProvidersType) {
            this.isSettingPublishedStatus = true;

            const adsText = `${this.deliverablesCount} ${this.deliverablesCount === 1 ? "ad" : "ads"}`;

            this.$snackbar.info(
                `Removing ${adsText} from ${this.PlatformLabel[publishProvider as keyof typeof this.PlatformLabel]} feeds`
            );
            this.toggleDropdown();

            let job: JobNotification | undefined;

            try {
                job = await unpublishDeliverables({
                    campaignId: this.campaignId,
                    publishProvider,
                    deliverables: this.deliverables,
                    libraryFilters: this.creativeLibraryFiltersStore.getComputedLibraryFilters
                });
            } catch (err) {
                this.$snackbar.error((err as Err).message);
                return;
            } finally {
                this.isSettingPublishedStatus = false;
            }

            const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                deliverablesCount: this.deliverablesCount,
                platform: publishProvider,
                status: NotificationStatus.InProgress,
                type: NotificationTypes.Unpublish,
                job
            });
            this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                jobId: job?._id,
                notificationId
            });
            this.$emit("publishedStatusHasBeenSet");
        },

        toggleDropdown() {
            this.dropdownIsVisible = !this.dropdownIsVisible;
        },

        async publishCOAdset() {
            const adsText = `${this.deliverablesCount} ${this.deliverablesCount === 1 ? "ad" : "ads"}`;
            this.isSettingPublishedStatus = true;

            this.$snackbar.info(`Adding ${adsText} to ${PlatformLabel[PLATFORM.OPENDC]} adset`);
            this.toggleDropdown();

            try {
                const uniqueIdsList = this.deliverables.map(
                    deliverable => `${deliverable.reportingLabel}_${deliverable.hash}`
                );
                await publishChoreographFeed(this.lastJob?._id, uniqueIdsList);
            } catch (err) {
                this.$snackbar.error((err as Err).message);
                return;
            } finally {
                this.isSettingPublishedStatus = false;
            }

            this.$snackbar.success(`Ads successfully sent to ${PlatformLabel[PLATFORM.OPENDC]}.`);
            this.$emit("refetchDeliverables");
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.rebuild-modal__info {
    padding-bottom: $spacing-smaller;
    font-weight: bold;
}
</style>
